<template>
    <drawer :title="title" :id="drawerName" size="sm" v-cloak>
        <h2>{{translate('vop_title')}}</h2>
        <p class="text-base text-grey-800">
            {{translate('vop_description', companyName)}}
        </p>
        <div class="mt-6">IBAN</div>
        <form action="" @submit.prevent="checkIban">
            <div  class="flex gap-5">
                <input type="text" v-model="ibanInput" :placeholder="placeHolder">

                <button type="submit" :disabled="!isValidFormat || isLoading || !hasAccess || (canAcceptTerms && !acceptTerms)" class="btn btn-primary w-40">
                    <template v-if="isLoading"><i class="far fa-spinner animate-spin"></i></template>
                    <template v-else><i class="far fa-check mr-1"></i> {{translate('verify')}}</template>
                </button>
            </div>

            <div v-if="canAcceptTerms" class="mt-3">
                <div class="flex gap-2">
                    <input type="checkbox" id="VopAkkoord" v-model="acceptTerms" required/>
                    <label for="@VopAkkoord">
                        <translation label="vop_terms">
                            <a href="https://www.surepay.eu/terms-of-service-2026-end-customers-through-partners/" target="_blank" class="link contents mx-1">
                                {{translate('de_gebruiksvoorwaarden').toLocaleLowerCase()}}
                            </a>
                        </translation>
                    </label>
                </div>
            </div>
        </form>

        <div class="mt-6">
            <div v-if="isLoading">
                <i class="far fa-spinner animate-spin text-lg text-primary-500"></i>
            </div>
            <div v-else-if="ibanInput && !isValidFormat" class="alert warning">
                {{translate('vop_invalid_iban')}}
            </div>

            <div v-else-if="isValidFormat && !hasAccess">
                <div class="alert success mb-2">
                    {{translate('vop_valid_iban')}}
                </div>

                <div class="alert info bg-primary-50 border-primary-200e py-6">
                    <div class="flex items-center gap-3 mb-2">
                        <strong class="text-primary-900 text-sm">{{translate('no_access')}}</strong>
                    </div>
                    <p class="text-sm text-primary-800 mb-3">
                        <translation label="vop_upgrade_text">
                            <strong>{{companyName}}</strong>
                        </translation>
                    </p>
                    <a href="/upgrade/vop" class="btn btn-primary">{{translate('upgrade_now')}} <i class="far fa-angle-right ml-1"></i></a>
                </div>
            </div>

            <div v-else-if="hasAccess && result">
                <div v-if="result.isMatch" class="alert success">
                    {{translate('vop_match')}}
                </div>
                <div v-else-if="result.isCloseMatch" class="alert warning" v-html="translate('vop_close_match', result.nameSuggestion).replace('\n', '<br>')">

                </div>
                <div v-else class="alert danger">
                    {{translate('vop_no_match')}}
                </div>
            </div>

            <div v-else-if="hasAccess && error">
                <div class="alert info bg-primary-50 border-primary-200e py-6">
                    <div class="flex items-center gap-3 mb-2">
                        <strong class="text-primary-900 text-sm">{{error}}</strong>
                    </div>
                    <p class="text-sm text-primary-800 mb-3">
                        <translation label="vop_add_credits">
                            <strong>{{companyName}}</strong>
                        </translation>
                    </p>
                    <a href="/upgrade/vop" class="btn btn-primary">{{translate('upgrade_now')}} <i class="far fa-angle-right ml-1"></i></a>
                </div>
            </div>
        </div>
    </drawer>
</template>

<script lang="ts">
import {VerificationOfPayeeResult} from "../types";
import {defineComponent}  from 'vue';
import {Drawer} from "../../../main-store";
import Ajax from "../../../lib/Ajax";
import Translation from "../../../components/translation.vue";
import {Feature} from "../../../types/dto/Feature";

export default defineComponent({
    name: "vop-drawer",
    components: {Translation},
    props: {
        hasAccess: Boolean,
        cwId: String,
        companyName: String,
        country: String
    },
    data() {
        return {
            title: this.translate('vop'),
            ibanInput: '',
            error: '',
            result: null as VerificationOfPayeeResult | null,
            isLoading: false,
            leadRequestSent: false,
            hasAcceptedTerms: window.cw.hasAcceptedVopTerms,
            acceptTerms: false
        }
    },
    watch: {
        isValidFormat(isValid: boolean) {
            if(isValid && !this.hasAccess && !this.leadRequestSent) {
                this.leadRequestSent = true;
                this.createFeatureVisitedLead(Feature.VOP);
            }
        },
        ibanInput(){
            this.result = null;
        }
    },
    computed: {
        canAcceptTerms(): boolean {
            return this.hasAccess && !this.hasAcceptedTerms;
        },
        drawerName(): Drawer{
            return this.store.Drawer.VERIFICATION_ON_PAYEE
        },
        isValidFormat(): boolean {
            const cleaned = this.ibanInput.replace(/\s+/g, '').toUpperCase();

            let regex: RegExp;

            switch (this.country?.toLowerCase()) {
                case 'nl':
                    regex = /^NL\d{2}[A-Z]{4}\d{10}$/;
                    break;

                case 'lu':
                    regex = /^LU\d{18}$/;
                    break;

                case 'fr':
                    regex = /^FR\d{25}$/;
                    break;

                default:
                    regex = /^BE\d{14}$/;
            }

            return regex.test(cleaned) && this.isValidIban(cleaned);
        },
        placeHolder(): string {
            switch (this.country?.toLowerCase()) {
                case 'nl': return "NL00 XXXX 00000000 00";
                case 'lu': return "LU00 0000 0000 0000 0000";
                case 'fr': return "FR00 0000 0000 0000 00000 0000 00";
            }

            return "BE00 0000 0000 0000";
        }
    },
    methods: {
        isValidIban(iban: string): boolean {
            const cleaned = iban.replace(/\s+/g, '').toUpperCase();
        
            const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
        
            let numeric = '';
            for (const ch of rearranged) {
                if (/[A-Z]/.test(ch)) {
                    numeric += (ch.charCodeAt(0) - 55).toString();
                } else {
                    numeric += ch;
                }
            }
        
            let remainder = 0;
            for (const digit of numeric) {
                remainder = (remainder * 10 + Number(digit)) % 97;
            }
        
            return remainder === 1;
        },
        async checkIban() {
            if(!this.ibanInput)
                return;
            try {
                this.isLoading = true;
                this.result = await Ajax.postAsync<VerificationOfPayeeResult>("/ajax/company/vop", {
                    cwId: this.cwId,
                    iban: this.ibanInput,
                    companyName: this.companyName
                });
                this.error = '';
            } catch (e: unknown) {
                this.error = e as string;
            } finally {
                this.isLoading = false;
            }
        }
    }
})
</script>